'use client';

import React, { useMemo } from 'react';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { usePerformanceLabsIndia } from './PerformanceLabsIndiaContext';
import { useCartStore, useShallow } from 'components/shared/Cart';
import { Cart } from 'assets/icons';
import { getPerformanceLabCartLines } from './performanceLabCartUtils';
import styles from './PLBottomBar.module.css';

type PLBottomBarProps = {
  $fixed?: boolean;
};

const PLBottomBar: React.FC<PLBottomBarProps> = ({ $fixed = false }) => {
  const pathname = useInternalPathname();
  const isGiftingPage =
    pathname.includes('/performance-lab/gift') ||
    pathname.includes('/performance-lab/gifting');

  const { cart, cartLoading, isCartEnabled } = usePerformanceLabsIndia();
  const cartContext = useCartStore(
    useShallow((s) => ({ showCart: s.showCart, checkout: s.checkout })),
  );

  const plLines = useMemo(() => getPerformanceLabCartLines(cart), [cart]);

  const plItemCount = useMemo(
    () =>
      plLines.reduce((sum: number, edge: any) => sum + edge.node.quantity, 0),
    [plLines],
  );

  const plTotal = useMemo(
    () =>
      plLines.reduce(
        (sum: number, edge: any) =>
          sum + Number(edge.node.cost.totalAmount.amount),
        0,
      ),
    [plLines],
  );

  // What the same cart would cost with no discount, so the bar can strike the
  // original through the way the plan cards do.
  //
  // `cost.totalAmount` is already net of Shopify's discount allocations, so the
  // original is recovered by adding them back rather than by re-deriving it
  // from our own card prices — Shopify is the authority on what is actually
  // being charged, and re-deriving is how a shown-vs-charged gap creeps in.
  const plOriginalTotal = useMemo(
    () =>
      plLines.reduce((sum: number, edge: any) => {
        const discounted = (edge.node.discountAllocations ?? []).reduce(
          (acc: number, allocation: any) =>
            acc + Number(allocation?.discountedAmount?.amount ?? 0),
          0,
        );
        return sum + Number(edge.node.cost.totalAmount.amount) + discounted;
      }, 0),
    [plLines],
  );

  // Only strike through when Shopify actually applied something — a rounding
  // artefact must not render a struck price identical to the one beside it.
  const hasCartDiscount = Math.round(plOriginalTotal) > Math.round(plTotal);

  const plVariantIds = useMemo(
    () =>
      plLines
        .map((edge: any) =>
          edge.node.merchandise.id.replace('gid://shopify/ProductVariant/', ''),
        )
        .join('-'),
    [plLines],
  );

  if (isGiftingPage) return null;
  if (!isCartEnabled) return null;
  if (plItemCount === 0) return null;

  const handleViewCart = () => {
    cartContext.showCart();
  };

  const handleCheckout = () => {
    if (cartLoading) return;
    cartContext.checkout();
  };

  return (
    <div
      className={styles.bottomBarContainer}
      data-fixed={$fixed ? 'true' : undefined}
    >
      {/* Mobile */}
      <div className={styles.mobileColumn}>
        <div className={styles.mobileTopRow}>
          <div className={styles.cartItemCount}>
            <Cart />
            <p>
              {plItemCount} {plItemCount === 1 ? 'item' : 'items'} added
            </p>
          </div>
          <div className={styles.cartTotal}>
            <span>₹{Math.round(plTotal).toLocaleString('en-IN')}</span>
            {hasCartDiscount && (
              <span className={styles.cartOriginalTotal}>
                ₹{Math.round(plOriginalTotal).toLocaleString('en-IN')}
              </span>
            )}
            {/* <TaxDisclaimer> (Tax incl.)</TaxDisclaimer> */}
          </div>
        </div>
        <p className={styles.bookingInfo}>
          After purchase, you&apos;ll receive an email to book your session
          slot.
        </p>
        <div className={styles.buttonRow}>
          <button
            className={`${styles.barButton} gtm`}
            data-buttontype="performance_lab_buy_view_cart"
            data-testid="pl-view-cart"
            data-product="performance-lab"
            data-variantid={plVariantIds}
            onClick={handleViewCart}
          >
            View Cart
          </button>
          <button
            className={`${styles.barButton} gtm`}
            data-buttontype="performance_lab_buy_checkout"
            data-testid="pl-checkout"
            data-product="performance-lab"
            data-variantid={plVariantIds}
            data-filled="true"
            onClick={handleCheckout}
            disabled={cartLoading}
          >
            Checkout
          </button>
        </div>
      </div>

      {/* Desktop */}
      <div className={styles.desktopRow}>
        <div>
          <div className={styles.cartItemCount}>
            <Cart />
            <p>
              {plItemCount} {plItemCount === 1 ? 'item' : 'items'} added
            </p>
          </div>
          <p className={styles.bookingInfo}>
            After purchase, you&apos;ll receive an email to book your session
            slot.
          </p>
        </div>
        <div className={styles.rightGroup}>
          <div className={styles.cartTotal}>
            <span>₹{Math.round(plTotal).toLocaleString('en-IN')}</span>
            {hasCartDiscount && (
              <span className={styles.cartOriginalTotal}>
                ₹{Math.round(plOriginalTotal).toLocaleString('en-IN')}
              </span>
            )}
            {/* <TaxDisclaimer> (Tax incl.)</TaxDisclaimer> */}
          </div>
          <div className={styles.buttonRow}>
            <button
              className={`${styles.barButton} gtm`}
              data-buttontype="performance_lab_buy_view_cart"
              data-testid="pl-view-cart"
              data-product="performance-lab"
              data-variantid={plVariantIds}
              onClick={handleViewCart}
            >
              View Cart
            </button>
            <button
              className={`${styles.barButton} gtm`}
              data-buttontype="performance_lab_buy_checkout"
              data-testid="pl-checkout"
              data-product="performance-lab"
              data-variantid={plVariantIds}
              data-filled="true"
              onClick={handleCheckout}
              disabled={cartLoading}
            >
              Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PLBottomBar;
